import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useIsFocused } from '@react-navigation/native';
import moment from 'moment';
import { showErrorToast, showSuccesToast } from '../../helpers';
import { useDispatch, useSelector } from 'react-redux';
import { tenantActions } from '../../../redux/tenant.actions';
import OpenFlowModal from '../../atoms/modal';
import BankAccountForm from './bankAccountform';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core';
import DialogButton from '@material-ui/core/Button';
import { TenantService } from '../../../redux/tenant.service';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../../../../constants/theme';

const BankAccountTable = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [Accounts, setAccounts] = useState([]);
  const dispatch = useDispatch();
  const bankAccounts = useSelector((state) => state?.tenant?.branchBankAccounts);
  const userDetails = useSelector((state) => state?.Login?.userDetails);
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  const [showSuccessModal, setSuccessModal] = useState(false);
  const [openAccountModal, setAccountModal] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);

  useEffect(() => {
    if (bankAccounts?.length) {
      setAccounts(bankAccounts);
    } else {
      setAccounts([]);
    }
  }, [bankAccounts]);

  const startFlow = () => {
    setAccountModal(true);
  };

  const filteredAccounts = Accounts?.filter((item) => {
    if (!searchQuery || !searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      item?.account_name?.toLowerCase().includes(q) ||
      item?.account_id?.toString().toLowerCase().includes(q) ||
      item?.sort_code?.toString().toLowerCase().includes(q)
    );
  });

  const _onAddNewBankAccount = (data) => {
    let payload = {
      sort_code: data?.sortCode,
      account_id: data?.accountNumber,
      account_name: data?.accountName,
      branch_id: userDetails?.hubita_id,
      branch: userDetails?.email,
      swift_code: data?.swiftCode,
      iban: data?.iban,
    };
    dispatch(
      tenantActions.saveBankAccount(payload, () => {
        setAccountModal(false);
        dispatch(tenantActions.getBranchBankAccounts());
      })
    );
  };

  const accountDetails = () => (
    <div>
      <BankAccountForm
        onSave={(data) => {
          _onAddNewBankAccount(data);
        }}
        onCancel={() => {
          setAccountModal(false);
        }}
      />
    </div>
  );

  const renderHeader = () => {
    if (isMobile) return null;

    return (
      <View style={styles.tableHeaderRow}>
        <Text style={[styles.headerCell, { flex: 1.2 }]}>ACCOUNT NAME</Text>
        <Text style={styles.headerCell}>ACCOUNT ID</Text>
        <Text style={styles.headerCell}>CREATED AT</Text>
        <Text style={styles.headerCell}>SORT CODE</Text>
        <Text style={styles.headerCell}>SWIFT</Text>
        <Text style={styles.headerCell}>IBAN</Text>
        <Text style={[styles.headerCell, { textAlign: 'right', flex: 0.8 }]}>ACTIONS</Text>
      </View>
    );
  };

  const renderMobileItem = (item) => {
    return (
      <View style={styles.mobileCard}>
        <View style={styles.mobileHeader}>
          <View style={styles.mobileTitleGroup}>
            <View style={styles.iconCircle}>
              <Feather name="credit-card" size={16} color="#6C2BD9" />
            </View>
            <Text style={styles.mobileTitle}>{item.account_name}</Text>
          </View>

          <TouchableOpacity
            onPress={() => {
              setDeleteItemId(item);
              setSuccessModal(true);
            }}
            style={styles.mobileDeleteBtn}
          >
            <Feather name="trash-2" size={16} color="#DC2626" />
          </TouchableOpacity>
        </View>

        <View style={styles.mobileDivider} />

        <View style={styles.mobileDetailsGrid}>
          <View style={styles.mobileDetailRow}>
            <Text style={styles.mobileLabel}>Account ID:</Text>
            <Text style={styles.mobileValue}>{item.account_id}</Text>
          </View>
          <View style={styles.mobileDetailRow}>
            <Text style={styles.mobileLabel}>Created At:</Text>
            <Text style={styles.mobileValue}>{moment(item.createdAt).format('YYYY-MM-DD')}</Text>
          </View>
          <View style={styles.mobileDetailRow}>
            <Text style={styles.mobileLabel}>Sort Code:</Text>
            <Text style={styles.mobileValue}>{item?.sort_code || '-'}</Text>
          </View>
          <View style={styles.mobileDetailRow}>
            <Text style={styles.mobileLabel}>SWIFT:</Text>
            <Text style={styles.mobileValue}>{item?.swift_code || '-'}</Text>
          </View>
          <View style={styles.mobileDetailRow}>
            <Text style={styles.mobileLabel}>IBAN:</Text>
            <Text style={styles.mobileValue}>{item?.iban || '-'}</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderDesktopItem = (item) => {
    return (
      <View style={styles.tableRow}>
        <Text style={[styles.dataCell, { flex: 1.2, fontWeight: '700', color: '#111827' }]}>
          {item.account_name}
        </Text>
        <Text style={styles.dataCell}>{item.account_id}</Text>
        <Text style={styles.dataCell}>{moment(item.createdAt).format('YYYY-MM-DD')}</Text>
        <Text style={styles.dataCell}>{item?.sort_code || '-'}</Text>
        <Text style={styles.dataCell}>{item?.swift_code || '-'}</Text>
        <Text style={styles.dataCell}>{item?.iban || '-'}</Text>
        <View style={[styles.dataCell, { flexDirection: 'row', justifyContent: 'flex-end', flex: 0.8, gap: 12 }]}>
          <TouchableOpacity
            onPress={() => {
              setDeleteItemId(item);
              setSuccessModal(true);
            }}
          >
            <Feather name="trash-2" size={16} color="#DC2626" />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <View>
      {/* Top Controls Row */}
      <View style={[styles.controlsRow, isMobile && styles.controlsRowMobile]}>
        <View style={styles.searchBox}>
          <Ionicons name="search-outline" size={16} color="#9CA3AF" style={{ marginRight: 8 }} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search account ID, sort code..."
            placeholderTextColor="#9CA3AF"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>

        <TouchableOpacity style={styles.addBtn} onPress={startFlow} activeOpacity={0.85}>
          <Text style={styles.addBtnText}>+ Add Bank Account</Text>
        </TouchableOpacity>
      </View>

      {/* Main Table / Cards Container */}
      <View style={[styles.cardContainer, isMobile && styles.cardContainerMobile]}>
        {filteredAccounts?.length ? (
          <>
            <FlatList
              data={filteredAccounts}
              keyExtractor={(item) => item?.account_id?.toString() || Math.random().toString()}
              ListHeaderComponent={renderHeader}
              renderItem={({ item }) => (isMobile ? renderMobileItem(item) : renderDesktopItem(item))}
            />

            {!isMobile && (
              <View style={styles.tableFooter}>
                <Text style={styles.tableFooterText}>
                  Showing 1-{filteredAccounts.length} of {filteredAccounts.length} bank accounts
                </Text>
                <View style={styles.pageBtnGroup}>
                  <TouchableOpacity style={styles.pageBtnDisabled} disabled={true}>
                    <Text style={styles.pageBtnDisabledText}>Previous</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.pageBtnDisabled} disabled={true}>
                    <Text style={styles.pageBtnDisabledText}>Next</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </>
        ) : (
          <View style={styles.emptyBox}>
            <Feather name="credit-card" size={32} color="#9CA3AF" style={{ marginBottom: 8 }} />
            <Text style={styles.emptyText}>No bank accounts added yet.</Text>
            <TouchableOpacity style={[styles.addBtn, { marginTop: 12 }]} onPress={startFlow}>
              <Text style={styles.addBtnText}>+ Add Bank Account</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {openAccountModal && (
        <OpenFlowModal
          showModal={openAccountModal}
          setShowModal={setAccountModal}
          modalBody={accountDetails()}
          showFooter={false}
          hideCloseIcon={true}
          modalHeading=""
          modalWidth={600}
        />
      )}

      <Dialog
        open={showSuccessModal}
        onClose={() => {
          setSuccessModal(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ marginBottom: 10 }}>
            Are you sure you want to delete this bank account?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <DialogButton
            onClick={() => {
              if (deleteItemId) {
                TenantService.deleteBankAccount(deleteItemId?.openflow_bank_account_id)
                  .then(() => {
                    setSuccessModal(false);
                    showSuccesToast('Account deleted successfully');
                    dispatch(tenantActions.getBranchBankAccounts());
                  })
                  .catch(() => {
                    setSuccessModal(false);
                    showErrorToast('Something went wrong!');
                  });
              }
            }}
            color="primary"
          >
            Ok
          </DialogButton>
          <DialogButton
            onClick={() => {
              setSuccessModal(false);
            }}
            color="secondary"
          >
            Cancel
          </DialogButton>
        </DialogActions>
      </Dialog>
    </View>
  );
};

export default BankAccountTable;

const styles = StyleSheet.create({
  controlsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 16,
  },
  controlsRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },

  searchBox: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 12,
    height: 40,
    ...SHADOWS.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: '#111827',
    outlineStyle: 'none',
  },

  addBtn: {
    backgroundColor: '#6C2BD9',
    borderRadius: BORDER_RADIUS.md,
    height: 40,
    paddingHorizontal: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#FFFFFF',
  },

  /* CARD CONTAINER */
  cardContainer: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: 16,
    ...SHADOWS.sm,
    marginBottom: 40,
  },
  cardContainerMobile: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    padding: 0,
    shadowColor: 'transparent',
  },

  /* TABLE DESKTOP */
  tableHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  headerCell: {
    flex: 1,
    fontSize: 10,
    fontWeight: '700',
    color: '#9CA3AF',
    letterSpacing: 0.5,
  },

  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderColor: '#F3F4F6',
  },
  dataCell: {
    flex: 1,
    fontSize: 13,
    color: '#374151',
  },

  tableFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderColor: '#F3F4F6',
  },
  tableFooterText: {
    fontSize: 12,
    color: '#6B7280',
  },
  pageBtnGroup: {
    flexDirection: 'row',
    gap: 8,
  },
  pageBtnDisabled: {
    backgroundColor: '#F3F4F6',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 12,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pageBtnDisabledText: {
    fontSize: 12,
    color: '#9CA3AF',
    fontWeight: '600',
  },

  /* MOBILE CARD */
  mobileCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    marginBottom: 12,
    ...SHADOWS.sm,
  },
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  mobileTitleGroup: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  mobileTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111827',
  },
  mobileDeleteBtn: {
    padding: 4,
  },
  mobileDivider: {
    height: 1,
    backgroundColor: '#F3F4F6',
    marginVertical: 12,
  },

  mobileDetailsGrid: {
    gap: 6,
  },
  mobileDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  mobileLabel: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
  mobileValue: {
    fontSize: 12,
    fontWeight: '600',
    color: '#111827',
  },

  emptyBox: {
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: '#6B7280',
  },
});